import { FORMS, MODULES } from "../../const";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useUserCompany from "../../hooks/useUserCompany";
import { getBulkDropdownData } from "../../api/keyvalue";
import { errorActions } from "../../store/error-slice";
import { toast } from "react-toastify";
import { errorMessageToast } from "../../helper";
import { Search, Trello } from "react-feather";
import Button from "../../components/input/Button";
import LoadingSpinner from "../../components/LoadingSpinner";
import Dropdown from "../../components/input/Dropdown";
import { Select } from "semantic-ui-react";
import Grid from "../../components/Grid";
import {
  ActiveCellStyle,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import SideDrawer from "../../components/input/SideDrawer";
import Tab from "../../components/Tab";
import {
  addOptionData,
  optionTypeDropdown,
  searchOptions,
  getOptionsByType,
} from "../../api/OptionConfigApi";
import OptionAddComponent from "../../components/admin/OptionAddComponent";

const FORM = FORMS.OPTION_CONFIG.OPT.form;

const OptionConfig = () => {
  const { firstViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Admin,
    section: FORMS.OPTION_CONFIG,
  });

  const [tab, setTab] = useState(1);
  const [drawerTab, setDrawerTab] = useState(1);
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(null);
  const dispatch = useDispatch();
  const errorState = useSelector((state) => state.error.optionConfigErrors);
  const [editMode, setEditMode] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);
  const [formData, setFormData] = useState({
    keyKey: null,
    keyValue1: null,
    keyCode: null,
    keySeq: null,
    keyDescription: null,
    status: null,
    active: null,
  });

  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [searchRes, setSearchRes] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [editOptions, setEditOptions] = useState({});
  const optionRef = useRef();
  const [optionTypesOpt, setOptionTypeOpt] = useState([]);
  const [currentErrors, setCurrentErrors] = useState({});

  const userCompany = useUserCompany();
  const user = useSelector((state) => state.user);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const { active = [], status = [] } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
      ]);

      const optionTypeData = await optionTypeDropdown();
      setOptionTypeOpt(optionTypeData);

      setActiveOptions(active);
      setStatusOptions(status);

      setIsLoading(false);
    })();
  }, []);

  const handleDrawerOpen = () => {
    setIsLoading(false);
    setIsDrawerOpen(true);
    setEditMode(false);
    setEditOptions(null);
    setDrawerTab(1);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const reset = () => {
    optionRef.current?.resetForm?.();
    setEditMode(false);
    setEditOptions(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setIsSubmitLoading(true);
    dispatch(errorActions.clearOptionError());

    const optionErrors = optionRef.current.validateForm?.() || {};
    if (Object.keys(optionErrors).length > 0) {
      dispatch(errorActions.validateOptions(optionErrors));
      setIsSubmitLoading(false);
      toast.error("There was an error adding Options", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    }

    try {
      const { created, modified, deleted } = optionRef.current.getBulkData();

      const withAuthor = (arr) =>
        (arr || []).map((item) => ({
          ...item,
          author: user.username,
        }));

      const bulkPayload = {
        created: withAuthor(created),
        modified: withAuthor(modified),
        deleted: deleted || [],
      };

      if (
        !bulkPayload.created.length &&
        !bulkPayload.modified.length &&
        !bulkPayload.deleted.length
      ) {
        toast.info("No changes to save.", {
          autoClose: 4000,
          containerId: "Main",
        });
        setIsSubmitLoading(false);
        return;
      }

      await addOptionData(bulkPayload);

      let msg = "Saved successfully!";
      if (
        bulkPayload.created.length &&
        !bulkPayload.modified.length &&
        !bulkPayload.deleted.length
      ) {
        msg =
          bulkPayload.created.length === 1
            ? "Added successfully!"
            : `${bulkPayload.created.length} records added successfully!`;
      } else if (
        bulkPayload.modified.length &&
        !bulkPayload.created.length &&
        !bulkPayload.deleted.length
      ) {
        msg =
          bulkPayload.modified.length === 1
            ? "Updated successfully!"
            : `${bulkPayload.modified.length} records updated successfully!`;
      } else {
        const parts = [];
        if (bulkPayload.created.length)
          parts.push(`${bulkPayload.created.length} added`);
        if (bulkPayload.modified.length)
          parts.push(`${bulkPayload.modified.length} updated`);
        if (bulkPayload.deleted.length)
          parts.push(`${bulkPayload.deleted.length} deleted`);
        msg = `Saved: ${parts.join(", ")}.`;
      }

      toast.success(msg, { autoClose: 5000, containerId: "Main" });
      handleDrawerClose();
      reset();
    } catch (e) {
      errorMessageToast(e);
    }

    setIsSubmitLoading(false);
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();

    if (tab === 1) {
      setIsLoading(false);
      setActiveAccordion1(false);
      setResetVisible(true);
      setSearchClicked(true);
      setSearchSubmitLoading(true);
    }

    const normalizeValue = (val) =>
      val === "" || val === undefined ? null : val;

    const response = await searchOptions({
      keyKey: normalizeValue(formData.keyKey),
      keyValue1: null,
      keyStatus: normalizeValue(status),
      keyActive: normalizeValue(active),
    });

    setSearchRes(response);
    setSearchSubmitLoading(false);
  };

  const handleSearchReset = (e) => {
    e.preventDefault();
    setStatus(null);
    setActive(null);
    setFormData({
      keyKey: null,
      keyValue1: null,
      keySeq: null,
      keyCode: null,
      keyDescription: null,
      keyStatus: null,
      keyActive: null,
    });
  };

  return (
    <>
      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div className="ui tabular menu attached">
          <div
            className={`item ${tab === 1 ? "active" : ""}`}
            onClick={() => {
              setTab(1);
            }}
          >
            <span>
              <Trello size={15} style={{ marginRight: "5px" }} />
            </span>
            Option Config Search
          </div>
        </div>
        <Button
          template="drawerOpen"
          onClick={handleDrawerOpen}
          authenticated={true}
          form={FORMS.OPTION_CONFIG.OPT.form}
          module={MODULES.Admin}
          editMode={false}
        />
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion1 ? "active" : ""}`}
            onClick={() => setActiveAccordion1(!activeAccordion1)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Search</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>

              <i
                className="refresh icon primary button"
                style={{
                  display: `${resetVisible ? "flex" : "none"}`,
                  cursor: "pointer",
                }}
                onClick={(e) => {}}
              ></i>
            </div>
          </div>
          <div className={`content ${activeAccordion1 ? "active" : ""}`}>
            {isSearchLoading ? (
              <LoadingSpinner />
            ) : (
              <form className="ui form">
                <h4 className="ui diving header">Option Config Information</h4>
                <div className="three fields">
                  <Dropdown
                    label="Option Type"
                    error={currentErrors.keyKey}
                    module={MODULES.Admin}
                    form={FORM}
                    field="key_key"
                    editMode={editMode}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { keyKey: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    clearable
                    placeholder="Option Type"
                    style={{ height: "5px" }}
                    options={optionTypesOpt}
                    value={formData.keyKey}
                    onChange={(e, data) => {
                      setFormData({
                        ...formData,
                        keyKey: data.value,
                      });
                    }}
                  />
                  <div className="field">
                    <label>Status</label>
                    <Select
                      options={statusOptions}
                      placeholder="Status"
                      style={{ height: "20px" }}
                      value={status}
                      onChange={(e, data) => {
                        setStatus(data.value);
                      }}
                    />
                  </div>

                  <div className="field">
                    <label>Active</label>
                    <Select
                      options={activeOptions}
                      placeholder="Active"
                      style={{ height: "20px" }}
                      value={active}
                      onChange={(e, data) => {
                        setActive(data.value);
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div className="field" style={{ width: "100%" }}>
                    <Button
                      template="search"
                      onClick={(e) => {
                        handleSearchSubmit(e);
                      }}
                    />
                    <Button
                      template="searchReset"
                      onClick={(e) => {
                        handleSearchReset(e);
                      }}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
        {searchClicked && (
          <>
            {searchSubmitLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                isLoading={searchFetchLoading}
                content={searchRes}
                config={[
                  {
                    headerName: "Type",
                    field: "keyKey",
                  },
                  {
                    headerName: "Status",
                    field: "statusLabel",
                    cellRenderer: StatusCellStyle,
                  },
                  {
                    headerName: "Active",
                    field: "activeLabel",
                    cellRenderer: ActiveCellStyle,
                  },
                ]}
                onCellDoubleClicked={async (props) => {
                  setSearchFetchLoading(true);
                  const type = props.data.keyKey;
                  const detail = await getOptionsByType(type, {
                    keyActive: null,
                  });
                  setEditOptions({ keyKey: type, detail });
                  setEditMode(true);
                  setIsDrawerOpen(true);
                  setSearchFetchLoading(false);
                }}
              />
            )}
          </>
        )}
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <>
              <div
                className="option-code-header"
                style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
              >
                <h3>
                  Option Type : <span></span>
                  {editOptions?.keyKey}
                </h3>
              </div>
            </>
          ) : null
        }
        body={
          <form className="drawer-content" onSubmit={handleSubmit}>
            <div style={{ width: "60vw", boxShadow: "none" }}>
              <div className="ui top attached tabular menu">
                <Tab
                  Icon={Trello}
                  onClick={() => {
                    setDrawerTab(1);
                  }}
                  active={drawerTab === 1}
                  errors={
                    Object.keys(errorState.optionConfigErrors).length !== 0
                  }
                  label="Option Config"
                  authenticated={true}
                  form={FORMS.OPTION_CONFIG.OPT_DETAIL.form}
                  showMandatory={true}
                  isMandatory={true}
                />
              </div>
              <div
                className="ui bottom attached active tab segment"
                style={{ display: drawerTab === 1 ? "block" : "none" }}
              >
                <OptionAddComponent
                  ref={optionRef}
                  isLoading={isLoading}
                  errors={errorState.optionConfigErrors}
                  dropdown={{
                    optionTypesOpt,
                    statusOptions,
                    activeOptions,
                  }}
                  editMode={editMode}
                  currentValues={editMode ? editOptions : null}
                />
              </div>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Button
                    type="submit"
                    template="create"
                    isLoading={isSubmitLoading}
                    create={!editMode}
                    form={FORMS.OPTION_CONFIG.OPT.form}
                    module={MODULES.Admin}
                    section={FORMS.OPTION_CONFIG}
                    authenticated={true}
                    editMode={editMode}
                  />
                </div>
              </div>
            </div>
          </form>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </>
  );
};

export default OptionConfig;
